/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { createContext, useCallback, useContext, useRef, useState, type ReactNode, type RefObject } from 'react';
import { ModalAwareScrollView } from './ModalAwareScrollView';
import { Modal } from './Modal';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  useWindowDimensions,
  View,
  type DimensionValue,
  type ScrollViewProps,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing } from '../theme/public-api';
import { WEB_SIDEBAR_BREAKPOINT } from '../layout/breakpoints';
import { useModalBackdropPadding } from '../ui/hooks-ui/useModalBackdropPadding';
import { ModalScreenHeader } from './ModalScreenHeader';
import { ModalHeroHeader } from './ModalHeroHeader';
import { TicketModalHeader } from '../ticket/TicketModalHeader';
import { modalShellStyles } from './ModalShell.styles';

export type ModalShellWidth = 'standard' | 'comfortable' | 'compact' | 'wide';

const widthMap: Record<ModalShellWidth, ViewStyle> = {
  standard: { maxWidth: 540 },
  comfortable: { maxWidth: 640 },
  compact: { maxWidth: 560 },
  // ~860px: forms densos de stock (Ingresar stock en desktop va a 2 columnas).
  wide: { maxWidth: 860 },
};

type Props = {
  visible: boolean;
  onClose: () => void;
  onRequestClose?: () => void;

  /** Título del modal (renderiza ModalScreenHeader). */
  title?: string;
  /** Kicker uppercase ink3 del header (estilo Square module header). */
  kicker?: string;
  /** Subtítulo opcional del header. */
  subtitle?: string | null;
  /** Si 'ticket', renderiza TicketModalHeader (kicker teal + título grande + borde). */
  appearance?: 'default' | 'ticket';
  /** Accessibility label para el botón de cierre. */
  closeAccessibilityLabel?: string;

  /** Si se pasa, renderiza ModalHeroHeader con kicker/subtitle/badge. */
  heroKicker?: string;
  heroTitle?: string;
  heroSubtitle?: string | null;
  heroCount?: number;
  heroCountLabel?: string;
  heroBadgeText?: string;

  /** Ancho máximo del card. Default: 'standard' (540). */
  widthVariant?: ModalShellWidth;

  /** Acciones del footer (botones). Si null, no renderiza footer. */
  footer?: ReactNode;

  /** Si false, no usa keyboard avoiding (para modales sin inputs). Default: true. */
  keyboardAvoiding?: boolean;

  /** Si false, no cierra al tocar el backdrop. Default: true. */
  dismissOnBackdropPress?: boolean;

  /** Si false, el body no scrollea (forms cortos: Varios, recarga). Default: true. */
  scrollable?: boolean;

  /** Props extra para el ScrollView interno. */
  scrollProps?: Omit<ScrollViewProps, 'style' | 'contentContainerStyle'>;

  /** Estilo extra para el ScrollView. */
  scrollStyle?: StyleProp<ViewStyle>;
  /** Estilo extra para el contentContainer del ScrollView. */
  scrollContentStyle?: StyleProp<ViewStyle>;

  /** Altura máxima del card. Si es number (px) pisa el tope automático (~92vh). */
  maxHeight?: DimensionValue;

  /** Si se pasa, usa este scrollRef en lugar de uno interno. */
  scrollRef?: RefObject<ScrollView | null>;
  /** Si se pasa, usa este contentRef en lugar de uno interno. */
  contentRef?: RefObject<View | null>;

  /** bottomOffset para ModalAwareScrollView (spacing inferior del input visible). */
  scrollBottomInset?: number;
  /** topInset para compatibilidad (no usado con ModalAwareScrollView). */
  scrollTopInset?: number;
  /** padding para compatibilidad (no usado con ModalAwareScrollView). */
  scrollPadding?: number;

  /** Estilo extra para el card. */
  cardStyle?: StyleProp<ViewStyle>;

  /** animationType del Modal. Default: 'fade'. */
  animationType?: 'fade' | 'slide' | 'none';

  /** Capa encima del card (confirm overlay) — dentro del Modal nativo. */
  overlay?: ReactNode;

  children: ReactNode;
};

type ScrollInputIntoViewFn = (inputRef?: RefObject<TextInput | null>) => void;
const NoopScroll: ScrollInputIntoViewFn = () => {};
const ModalShellScrollContext = createContext<ScrollInputIntoViewFn>(NoopScroll);

export function useModalShellScroll(): ScrollInputIntoViewFn {
  return useContext(ModalShellScrollContext);
}

export function ModalShell({
  visible,
  onClose,
  onRequestClose,
  title,
  kicker,
  subtitle,
  appearance,
  closeAccessibilityLabel,
  heroKicker,
  heroTitle,
  heroSubtitle,
  heroCount,
  heroCountLabel,
  heroBadgeText,
  widthVariant = 'standard',
  footer,
  keyboardAvoiding = true,
  dismissOnBackdropPress = true,
  scrollable = true,
  scrollProps,
  scrollStyle,
  scrollContentStyle,
  maxHeight = '96%',
  scrollRef: externalScrollRef,
  contentRef: externalContentRef,
  scrollBottomInset = spacing.sm,
  scrollTopInset = 0,
  scrollPadding = spacing.md,
  cardStyle,
  animationType,
  overlay = null,
  children,
}: Props) {
  const modalBackdropPadding = useModalBackdropPadding();
  const insets = useSafeAreaInsets();
  const { width: winW, height: winH } = useWindowDimensions();
  const internalScrollRef = useRef<ScrollView>(null);
  const internalContentRef = useRef<View>(null);
  const scrollRef = externalScrollRef ?? internalScrollRef;
  const contentRef = externalContentRef ?? internalContentRef;
  const bodyLayoutH = useRef(0);
  const bodyContentH = useRef(0);
  const [bodyOverflows, setBodyOverflows] = useState(false);

  const syncBodyOverflow = useCallback(() => {
    // Umbral: evita micro-scroll por redondeo/paddings (~formPanel gris).
    const next = bodyContentH.current > bodyLayoutH.current + 8;
    setBodyOverflows((prev) => (prev === next ? prev : next));
  }, []);

  const handleBackdropPress = () => {
    if (dismissOnBackdropPress) onClose();
  };

  const showHeader = title != null;
  const showHero = heroKicker != null;

  const cardWidthStyle = widthMap[widthVariant];
  const isMobile = winW < WEB_SIDEBAR_BREAKPOINT;
  const effectiveAnimation = animationType ?? (isMobile ? 'slide' : 'fade');
  const safeBottom = Math.max(insets.bottom, spacing.sm);
  // Tope en px (no %): en web/mobile el sheet alineado abajo con maxHeight %
  // crece con el contenido, empuja el header fuera de viewport y el scroll
  // nunca se activa (layoutH === contentH).
  const cardMaxHeightPx = Math.round(winH * 0.92);
  const chromeBudget =
    (showHeader ? 56 : 0) +
    (showHero ? 64 : 0) +
    (footer != null ? 96 : 0) +
    (isMobile ? 28 : 0) + // grabber
    safeBottom +
    8;
  const scrollMaxHeight = Math.max(160, cardMaxHeightPx - chromeBudget);

  const backdropStyle = isMobile
    ? modalShellStyles.bottomSheetBackdrop
    : [modalShellStyles.backdrop, modalBackdropPadding];

  const cardStyleBase = isMobile
    ? [modalShellStyles.bottomSheetCard, { paddingBottom: safeBottom, maxHeight: cardMaxHeightPx }]
    : [modalShellStyles.card, cardWidthStyle, { maxHeight: cardMaxHeightPx }];

  // Mobile + teclado: KeyboardAvoidingView afuera. No usar KASV acá — su wrapper trae
  // flexGrow:1 y genera scroll mínimo en forms cortos (Varios, recarga, etc.).
  const useKasv = keyboardAvoiding && !isMobile && scrollable;
  const ScrollComponent = useKasv ? ModalAwareScrollView : ScrollView;

  const bodyContentStyle = [
    isMobile ? modalShellStyles.bottomSheetScrollContent : modalShellStyles.scrollContent,
    scrollContentStyle,
  ];

  const body = !scrollable ? (
    <View style={[modalShellStyles.scroll, isMobile && modalShellStyles.bottomSheetScroll, scrollStyle, bodyContentStyle]}>
      <View ref={contentRef} collapsable={false}>
        <ModalShellScrollContext.Provider value={NoopScroll}>
          {children}
        </ModalShellScrollContext.Provider>
      </View>
    </View>
  ) : (
        <ScrollComponent
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ref={scrollRef as any}
          style={[
            modalShellStyles.scroll,
            isMobile && modalShellStyles.bottomSheetScroll,
            { maxHeight: scrollMaxHeight },
            scrollStyle,
          ]}
          contentContainerStyle={bodyContentStyle}
          keyboardShouldPersistTaps="handled"
          nestedScrollEnabled
          {...(useKasv ? { bottomOffset: scrollBottomInset } : {})}
          {...scrollProps}
          scrollEnabled={bodyOverflows}
          bounces={bodyOverflows}
          showsVerticalScrollIndicator={bodyOverflows}
          onLayout={(e) => {
            scrollProps?.onLayout?.(e);
            bodyLayoutH.current = e.nativeEvent.layout.height;
            syncBodyOverflow();
          }}
          onContentSizeChange={(w, h) => {
            scrollProps?.onContentSizeChange?.(w, h);
            bodyContentH.current = h;
            syncBodyOverflow();
          }}
        >
          <View ref={contentRef} collapsable={false}>
            <ModalShellScrollContext.Provider value={NoopScroll}>
              {children}
            </ModalShellScrollContext.Provider>
          </View>
        </ScrollComponent>
  );

  const card = (
      <View
        style={[
          cardStyleBase,
          // Prefer px cap; allow caller override if they pass a number.
          typeof maxHeight === 'number' ? { maxHeight } : null,
          cardStyle,
        ]}
      >
        {isMobile ? <View style={modalShellStyles.grabber} /> : null}

        {showHeader ? (
          <View style={isMobile && appearance !== 'ticket' ? modalShellStyles.bottomSheetHeader : undefined}>
            {appearance === 'ticket' ? (
              <TicketModalHeader
                kicker={kicker}
                title={title}
                subtitle={subtitle ?? undefined}
                onClose={onClose}
                compact={isMobile}
              />
            ) : (
              <ModalScreenHeader
                title={title}
                kicker={kicker}
                subtitle={subtitle}
                onClose={onClose}
                closeAccessibilityLabel={closeAccessibilityLabel}
              />
            )}
          </View>
        ) : null}

        {showHero ? (
          <ModalHeroHeader
            kicker={heroKicker}
            title={heroTitle}
            subtitle={heroSubtitle}
            count={heroCount}
            countLabel={heroCountLabel}
            badgeText={heroBadgeText}
          />
        ) : null}

        {body}

        {footer != null ? (
          <View style={isMobile ? modalShellStyles.bottomSheetFooter : modalShellStyles.footer}>
            {footer}
          </View>
        ) : null}
      </View>
  );

  // Overlay (confirm) va al root del Modal, no adentro del card: overflow/clip
  // del sheet hacía que el confirm no se viera o no recibiera taps (Liberar “muerto”).
  const inner = (
    <View style={backdropStyle}>
      <Pressable style={StyleSheet.absoluteFill} onPress={handleBackdropPress} />
      {isMobile && keyboardAvoiding ? (
        <KeyboardAvoidingView behavior="padding" style={modalShellStyles.keyboardSheetWrap} automaticOffset>
          {card}
        </KeyboardAvoidingView>
      ) : (
        card
      )}
      {overlay != null ? (
        <View
          style={[StyleSheet.absoluteFill, { zIndex: 9999, elevation: 9999 }]}
          pointerEvents="box-none"
        >
          {overlay}
        </View>
      ) : null}
    </View>
  );

  return (
    <Modal
      visible={visible}
      transparent
      animationType={effectiveAnimation}
      onRequestClose={onRequestClose ?? onClose}
    >
      {inner}
    </Modal>
  );
}
